/**
 * Tour helpers: DOM resolution for `data-tour` targets (including breakpoint visibility), route checks,
 * controlled Joyride event mapping, and scroll lock while overlays run.
 */
import type { Placement } from '@floating-ui/dom';
import { ACTIONS, type EventData, EVENTS, type Step, STATUS } from 'react-joyride';
import type { ControlledJoyrideEffect, JoyrideFinishReason } from './productOnboarding.types';

const JOYRIDE_SCROLL_LOCK_CLASS = 'joyride-tour-scroll-lock';

/**
 * While a Joyride tour runs, lock page scroll (Layout uses `main.overflow-y-auto` and job pages add
 * inner `overflow-y-auto`; users should not scroll behind the overlay).
 */
export function setJoyrideScrollLock(active: boolean): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.documentElement.classList.toggle(JOYRIDE_SCROLL_LOCK_CLASS, active);
}

export const JOB_DETAILS_TOUR_FIRST_SELECTOR = '[data-tour="job-details-panel"]';

/** First anchor for starting the job-details tour (desktop panel, or mobile fallbacks). */
export function firstJobDetailsTourAnchor(): HTMLElement | null {
  return (
    firstVisibleTourTarget(JOB_DETAILS_TOUR_FIRST_SELECTOR) ??
    firstVisibleTourTarget('[data-tour="candidates-panel"]') ??
    firstVisibleTourTarget('[data-tour="add-cv-btn"]')
  );
}

export const TOUR_FLIP_FALLBACKS: Placement[] = ['left', 'right', 'bottom-start', 'bottom-end', 'top'];

export function isJobDetailsRoute(pathname: string): boolean {
  return /^\/job\/[^/]+\/?$/.test(pathname);
}

/** Full-page exam editor route (`/jobs/:jobId/exam-config`). */
export function isExamConfigRoute(pathname: string): boolean {
  return /^\/jobs\/[^/]+\/exam-config\/?$/.test(pathname);
}

export function parseJobIdFromPath(pathname: string): string | null {
  const job = pathname.match(/^\/job\/([^/]+)/);
  if (job) {
    return job[1];
  }
  const exam = pathname.match(/^\/jobs\/([^/]+)\/exam-config/);
  return exam ? exam[1] : null;
}

/**
 * Joyride uses `document.querySelector` for string targets; the first match can be inside a
 * `display:none` breakpoint (e.g. mobile overflow menu vs desktop buttons). Resolve the first
 * element that is actually visible so the tour does not spin a loader and reset.
 */
export function firstVisibleTourTarget(selector: string): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const list = document.querySelectorAll<HTMLElement>(selector);
  for (let i = 0; i < list.length; i++) {
    const el = list[i];
    if (!el.isConnected) {
      continue;
    }

    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      continue;
    }

    let p: HTMLElement | null = el;
    let hiddenByAncestor = false;
    while (p) {
      const cs = getComputedStyle(p);
      if (cs.display === 'none' || cs.visibility === 'hidden') {
        hiddenByAncestor = true;
        break;
      }
      p = p.parentElement;
    }

    if (!hiddenByAncestor) {
      return el;
    }
  }

  return null;
}

export function hasTourTarget(selector: string): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  return document.querySelector(selector) !== null;
}

export function isStringTarget(t: Step['target']): t is string {
  return typeof t === 'string';
}

export function readCssColor(variableName: string, fallback: string): string {
  if (typeof window === 'undefined') {
    return fallback;
  }
  const value = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
  return value || fallback;
}

/**
 * Controlled `stepIndex` + clamping next to the last step means Joyride never finishes on its own
 * when the user taps Done, the overlay would stay. Maps raw events to finish vs next index.
 */
export function effectFromControlledJoyrideEvent(
  data: EventData,
  stepCount: number
): ControlledJoyrideEffect {
  const lastIndex = stepCount - 1;
  const { action, index, type, status } = data;

  const finish = (reason: JoyrideFinishReason): ControlledJoyrideEffect => ({ kind: 'finish', reason });

  if (status === STATUS.SKIPPED || action === ACTIONS.SKIP) {
    return finish('aborted');
  }

  if (action === ACTIONS.CLOSE) {
    return finish('aborted');
  }

  if (type === EVENTS.TOUR_END || status === STATUS.FINISHED) {
    return finish('completed');
  }

  if (type !== EVENTS.STEP_AFTER && type !== EVENTS.TARGET_NOT_FOUND) {
    return { kind: 'none' };
  }

  const goingBack = action === ACTIONS.PREV;
  if (type === EVENTS.TARGET_NOT_FOUND) {
    return { kind: 'none' };
  }

  if (type === EVENTS.STEP_AFTER && index >= lastIndex && !goingBack) {
    return finish('completed');
  }

  const next = goingBack ? Math.max(index - 1, 0) : Math.min(index + 1, lastIndex);
  return { kind: 'set_index', value: next };
}
